import { Hash, Loader2, Pin, Search, Settings, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useOnlineUserIds } from '../../hooks/usePresence';
import {
  useConversationReceipts,
  useMarkConversationRead,
  useOpenDm,
  useTeamChatMessages,
  useTeamChatPins,
} from '../../hooks/useTeamChat';
import type { TeamChatMessage, TeamConversation } from '../../lib/teamChatApi';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../tasks/linear/Avatar';
import { Skeleton } from '../ui/Skeleton';
import { ChatSearchPanel } from './ChatSearchPanel';
import { Composer } from './Composer';
import { GroupSettingsModal } from './GroupSettingsModal';
import { MemberListPanel } from './MemberListPanel';
import { MemberProfilePanel } from './MemberProfilePanel';
import { MessageList } from './MessageList';
import { PinnedPanel } from './PinnedPanel';
import { conversationTitle } from './TeamChatSidebar';
import { useJumpToMessage } from './jumpToMessage';
import { Popover } from '../tasks/linear/Popover';

const MEMBERS_KEY = 'cle:teamChatMembers';

function loadShowMembers(): boolean {
  try {
    return localStorage.getItem(MEMBERS_KEY) !== '0';
  } catch {
    return true;
  }
}

/** Header + messages + composer for one open conversation. */
export function ConversationView({ conversation }: { conversation: TeamConversation }) {
  const { t } = useI18n();
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openDm = useOpenDm();
  const onlineIds = useOnlineUserIds();
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(loadShowMembers);
  const [replyTo, setReplyTo] = useState<TeamChatMessage | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(MEMBERS_KEY, showMembers ? '1' : '0');
    } catch {
      /* private mode / quota — the toggle just won't survive a reload */
    }
  }, [showMembers]);

  const query = useTeamChatMessages(conversation.id);

  // Pages arrive newest-first; flatten and reverse into chronological order.
  const messages = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((page) => page.messages)
        .slice()
        .reverse(),
    [query.data],
  );

  const latestId = messages.length > 0 ? messages[messages.length - 1].id : null;
  useMarkConversationRead(conversation.id, latestId);

  // Everyone's read watermark, which is what colours the ticks on own messages.
  const { data: receipts } = useConversationReceipts(conversation.id);

  // Loaded even while the panel is closed so the header can show how many
  // messages are pinned. Pins live outside the loaded message pages, so this
  // count can't be derived from `messages`.
  const { data: pins } = useTeamChatPins(conversation.id, true);
  const pinCount = pins?.length ?? 0;

  const title = conversationTitle(conversation, me?.id);
  const memberNames = conversation.members.map((m) => m.name).join(', ');

  // Lives here rather than in MessageList because reaching an older message
  // means paging the query backwards, and the query is owned at this level.
  const fetchOlder = useCallback(
    async () => ({ hasNextPage: (await query.fetchNextPage()).hasNextPage }),
    [query],
  );
  const { jumpTo, jumping, missed } = useJumpToMessage(fetchOlder);

  // Arriving from a sidebar search hit: /team-chat/:id?m=<messageId>. Wait for
  // the first page to be on screen — jumping before that would page backwards
  // looking for a message that simply hasn't rendered yet. The param is dropped
  // immediately so a refresh, or the next 4s poll, doesn't yank the view back.
  const jumpParam = searchParams.get('m');
  useEffect(() => {
    if (!jumpParam || messages.length === 0) return;
    jumpTo(jumpParam);
    // Only `m` goes; an open profile (`?profile=`) rides along untouched.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('m');
        return next;
      },
      { replace: true },
    );
  }, [jumpParam, messages.length, jumpTo, setSearchParams]);

  const isGroup = conversation.type === 'GROUP';

  // The open profile lives in the URL (`?profile=<userId>`), not in state:
  // clicking a member in a group opens the DM with them, which is a different
  // conversation and a fresh view — anything held here would be gone. In the
  // URL it survives the switch, so the person is still on the right while the
  // message box for them is in the middle.
  const profileId = searchParams.get('profile');
  const closeProfile = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('profile');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const openMember = useCallback(
    (userId: string) => {
      // Yourself: nothing to write to, just the card.
      if (userId === me?.id) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('profile', userId);
            return next;
          },
          { replace: true },
        );
        return;
      }
      openDm.mutate(userId, {
        onSuccess: (dm) => navigate(`/team-chat/${dm.id}?profile=${userId}`),
      });
    },
    [me?.id, navigate, openDm, setSearchParams],
  );

  // Search and pins: a 320px popover from sm up; on a phone it spans the
  // screen less 8px a side instead of three quarters of it (the app does the
  // same).
  const headerPanelW = window.innerWidth < 640 ? window.innerWidth - 16 : 320;

  return (
    <div className="flex h-full min-h-0">
      <div className="relative flex min-w-0 flex-1 flex-col">
        {/* Header. `relative` is load-bearing: the pinned panel drops from
          `top-full`, and without a positioned header it anchored to the
          full-height column instead — i.e. rendered below the viewport, so
          pinning appeared to do nothing at all. */}
        <div className="relative flex items-center gap-2.5 border-b border-border px-4 py-3">
          {conversation.type === 'GROUP' ? (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent">
              <Hash size={15} className="text-muted-foreground" />
            </span>
          ) : (
            <Avatar name={title || null} size={28} />
          )}
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-heading text-sm font-semibold text-foreground">{title}</h1>
            {conversation.type === 'GROUP' && (
              <p className="truncate text-xs text-muted-foreground">{memberNames}</p>
            )}
          </div>
          {/* Own group with no gap on touch: there every icon is already a
              44px target (index.css), and the header's gap on top of that
              spread four 17px glyphs over half the width. -mr-2 lines the
              last glyph up with the px-4 edge. */}
          <div className="flex shrink-0 items-center gap-1 touch:-mr-2 touch:gap-0">
            <Popover
              align="end"
              width={headerPanelW}
              trigger={({ open, toggle, ref }) => (
                <button
                  type="button"
                  ref={ref}
                  onClick={toggle}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  aria-label={t('teamChat.searchInChat')}
                  title={t('teamChat.searchInChat')}
                  className={`rounded-md p-1.5 hover:bg-accent hover:text-foreground ${
                    open ? 'text-brand-600' : 'text-muted-foreground'
                  }`}
                >
                  <Search size={17} />
                </button>
              )}
            >
              {(close) => (
                <ChatSearchPanel
                  conversationId={conversation.id}
                  onJumpTo={jumpTo}
                  onClose={close}
                />
              )}
            </Popover>
            <Popover
              align="end"
              width={headerPanelW}
              trigger={({ open, toggle, ref }) => (
                <button
                  type="button"
                  ref={ref}
                  onClick={toggle}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  aria-label={t('teamChat.pinned')}
                  title={t('teamChat.pinned')}
                  className={`relative rounded-md p-1.5 hover:bg-accent hover:text-foreground ${
                    open || pinCount > 0 ? 'text-amber-500' : 'text-muted-foreground'
                  }`}
                >
                  <Pin size={17} />
                  {/* The count is the whole point of the affordance: without it
                  there is no sign from the header that anything has been pinned. */}
                  {pinCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-4 text-white">
                      {pinCount}
                    </span>
                  )}
                </button>
              )}
            >
              {(close) => (
                <PinnedPanel conversationId={conversation.id} onJumpTo={jumpTo} onClose={close} />
              )}
            </Popover>
            {isGroup && (
              <button
                type="button"
                onClick={() => setShowMembers((v) => !v)}
                aria-label={showMembers ? t('teamChat.hideMembers') : t('teamChat.showMembers')}
                title={showMembers ? t('teamChat.hideMembers') : t('teamChat.showMembers')}
                className={`hidden rounded-md p-1.5 hover:bg-accent hover:text-foreground lg:block ${
                  showMembers ? 'text-brand-600' : 'text-muted-foreground'
                }`}
              >
                <Users size={17} />
              </button>
            )}
            {isAdmin && conversation.type === 'GROUP' && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                aria-label={t('teamChat.groupSettings')}
                title={t('teamChat.groupSettings')}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Settings size={17} />
              </button>
            )}
          </div>
        </div>

        {/* Paging back to an old pinned message takes a request or three, and
            the list looks frozen meanwhile. */}
        {(jumping || missed) && (
          <div className="flex items-center justify-center gap-2 border-b border-border bg-accent/60 px-3 py-1.5 text-xs text-muted-foreground">
            {jumping ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                {t('teamChat.jumpLoading')}
              </>
            ) : (
              <span className="text-amber-600">{t('teamChat.jumpTooOld')}</span>
            )}
          </div>
        )}

        {/* Messages */}
        {query.isLoading ? (
          <div className="flex-1 space-y-4 overflow-hidden px-4 py-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <MessageList
            messages={messages}
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            fetchNextPage={() => void query.fetchNextPage()}
            meId={me?.id}
            isAdmin={isAdmin}
            receipts={receipts}
            isGroup={isGroup}
            onReply={setReplyTo}
            onJumpTo={jumpTo}
          />
        )}

        <Composer
          conversationId={conversation.id}
          placeholder={t('teamChat.messagePlaceholder', { name: title })}
          replyTo={replyTo}
          onClearReply={() => setReplyTo(null)}
        />

        {isAdmin && conversation.type === 'GROUP' && (
          <GroupSettingsModal
            key={`${conversation.id}-${showSettings}`}
            conversation={conversation}
            open={showSettings}
            onClose={() => setShowSettings(false)}
          />
        )}
      </div>

      {/* Right column: the open profile wins, in a DM as much as in a group;
          otherwise the roster, groups only — a DM has nobody else to list. */}
      {profileId ? (
        <MemberProfilePanel
          key={profileId}
          userId={profileId}
          online={onlineIds.has(profileId)}
          onBack={closeProfile}
          backLabel={isGroup ? undefined : t('teamChat.profile.close')}
        />
      ) : (
        isGroup &&
        showMembers && <MemberListPanel members={conversation.members} onMemberClick={openMember} />
      )}
    </div>
  );
}
